// Utility functions to export full API dataset as CSV or Excel (.xls)
import moment from "moment";

const formatValue = (val) => {
  if (val === null || val === undefined) return '""';
  let str = String(val).replace(/"/g, '""');
  return `"${str}"`;
};

const resolveVal = (item, key, idx) => {
  if (key === "id") {
    const num = idx + 1;
    return num < 10 ? `0${num}` : `${num}`;
  }

  let val = item[key];

  // Primary + Fallback property lookups
  if (key === "adminCommission" || key === "admin_commission" || key === "commission" || key === "commision") {
    val = item.adminCommission !== undefined ? item.adminCommission :
          (item.admin_commission !== undefined ? item.admin_commission :
          (item.commission !== undefined ? item.commission :
          (item.commision !== undefined ? item.commision :
          (item.commissionAmount !== undefined ? item.commissionAmount :
          (item.adminFee !== undefined ? item.adminFee :
          (item.fee !== undefined ? item.fee :
          (item.admin_fee !== undefined ? item.admin_fee : 0)))))));
  } else if (key === "charge" || key === "charges" || key === "fee" || key === "adminFee") {
    val = item.charge !== undefined ? item.charge :
          (item.charges !== undefined ? item.charges :
          (item.adminFee !== undefined ? item.adminFee :
          (item.fee !== undefined ? item.fee :
          (item.admin_fee !== undefined ? item.admin_fee : 0))));
  } else if (val === undefined || val === null) {
    if (key === "hash") val = item.transactionHash || item.hash || item.txHash || item.tx_hash;
    else if (key === "address") val = item.walletAddress || item.from || item.address || item.to;
    else if (key === "payable" || key === "payableAmount" || key === "payAbleAmount") {
      val = item.payAbleAmount !== undefined ? item.payAbleAmount :
            (item.payableAmount !== undefined ? item.payableAmount :
            (item.payable !== undefined ? item.payable : item.payable_amount));
    }
    else if (key === "remark" || key === "remarks") val = item.remark || item.remarks;
    else if (key === "date") val = item.createdAt ? moment(item.createdAt).format("DD-MM-YYYY hh:mm A") : (item.date ? moment(item.date).format("DD-MM-YYYY hh:mm A") : "");
    else if (key === "currency") val = item.tokenName || item.currency || "USDT";
    else if (key === "amount") val = item.amount !== undefined ? item.amount : item.value;
    else if (key === "value") val = item.value !== undefined ? item.value : item.amount;
  } else if (key === "date" || key === "createdAt") {
    val = moment(val).format("DD-MM-YYYY hh:mm A");
  }

  // Format numeric values cleanly with 2-3 decimal places (4 decimal places for adminCommission) and leading zero for numbers < 10
  if (typeof val === "number" || (typeof val === "string" && !isNaN(val) && val.trim() !== "" && key !== "id" && key !== "apiKey" && key !== "requestId" && key !== "walletAddress" && key !== "hash")) {
    const num = Number(val);
    if (!isNaN(num)) {
      const isCommission = key === "adminCommission" || key === "admin_commission" || key === "commission";
      const minDec = isCommission ? 4 : 2;
      const maxDec = isCommission ? 4 : 3;
      const absNum = Math.abs(num);
      const formatted = absNum.toLocaleString("en-US", {
        minimumFractionDigits: minDec,
        maximumFractionDigits: maxDec,
      });
      const prefix = absNum < 10 ? "0" : "";
      return num < 0 ? `-${prefix}${formatted}` : `${prefix}${formatted}`;
    }
  }

  return val !== undefined && val !== null ? val : "";
};

export const exportToCSV = (columns, data, filename = "transaction_ledger.csv") => {
  if (!data || data.length === 0) return;

  // Determine export columns (use provided columns or extract all keys from data)
  let exportCols = columns;
  if (!exportCols || exportCols.length === 0) {
    const keys = Object.keys(data[0]);
    exportCols = keys.map((k) => ({ key: k, label: k.toUpperCase() }));
  }

  const headers = exportCols.map((col) => formatValue(col.label)).join(",");
  const rows = data.map((item, idx) => {
    return exportCols
      .map((col) => {
        let val = resolveVal(item, col.key, idx);
        return formatValue(val);
      })
      .join(",");
  });

  const csvContent = "\uFEFF" + [headers, ...rows].join("\n");
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename.endsWith(".csv") ? filename : `${filename}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};


export const exportToExcel = (columns, data, filename = "transaction_ledger.xls") => {
  if (!data || data.length === 0) return;

  let exportCols = columns;
  if (!exportCols || exportCols.length === 0) {
    const keys = Object.keys(data[0]);
    exportCols = keys.map((k) => ({ key: k, label: k.toUpperCase() }));
  }

  let tableHtml = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40"><head><meta charset="UTF-8"><!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet><x:Name>Ledger</x:Name><x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions></x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]--></head><body><table border="1"><thead><tr>`;

  exportCols.forEach((col) => {
    tableHtml += `<th style="background-color:#161F1C; color:#6FE6B8; font-weight:bold; padding:8px;">${col.label}</th>`;
  });
  tableHtml += `</tr></thead><tbody>`;

  data.forEach((item, idx) => {
    tableHtml += `<tr>`;
    exportCols.forEach((col) => {
      let val = resolveVal(item, col.key, idx);
      tableHtml += `<td style="padding:6px;">${val !== undefined && val !== null ? val : "-"}</td>`;
    });
    tableHtml += `</tr>`;
  });

  tableHtml += `</tbody></table></body></html>`;

  const blob = new Blob([tableHtml], { type: "application/vnd.ms-excel;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename.endsWith(".xls") ? filename : `${filename}.xls`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Reusable handleExportData utility to fetch full dataset and export to CSV or Excel.
 */
export const handleExportData = async ({
  endpoint,
  params = {},
  columns = [],
  fallbackData = [],
  filename = "transaction_history",
  type = "csv",
  postDataFn,
  toastFn,
}) => {
  try {
    let allData = fallbackData;
    if (endpoint && typeof postDataFn === "function") {
      const res = await postDataFn(endpoint, { skip: "0", limit: "10000", ...params });
      allData = res?.data?.data || res?.data || fallbackData;
    }
    if (!allData || allData.length === 0) {
      if (typeof toastFn?.warn === "function") {
        toastFn.warn("No records found to export.");
      }
      return;
    }
    if (type === "excel") {
      exportToExcel(columns, allData, `${filename}.xls`);
    } else {
      exportToCSV(columns, allData, `${filename}.csv`);
    }
    if (typeof toastFn?.success === "function") {
      toastFn.success(`Exported ${allData.length} records successfully!`);
    }
  } catch (err) {
    console.error("Export error:", err);
    if (typeof toastFn?.error === "function") {
      toastFn.error("Failed to fetch records for export.");
    }
  }
};

